/**
 * GroupSeatManagement Component - DDD 리팩토링 버전
 * 그룹 좌석 관리 컴포넌트
 */
import React from 'react';
import {
  UserGroupIcon,
  PlusIcon,
  PencilIcon,
  TrashIcon,
  BuildingOfficeIcon,
  CurrencyDollarIcon,
  CheckCircleIcon,
  XMarkIcon,
  ExclamationTriangleIcon,
} from '@heroicons/react/24/outline';
import { useGroupSeatManagement } from '../use-cases/useGroupSeatManagement';
import { groupSeatManagementService } from '../services/GroupSeatManagementService';

const GroupSeatManagement: React.FC = () => {
  const {
    groups,
    availableSeats,
    loading,
    error,
    showModal,
    editingGroup,
    formData,
    setFormData,
    selectedSeats,
    toggleSeatSelection,
    openCreateModal,
    openEditModal,
    closeModal,
    handleSaveGroup,
    handleDeleteGroup,
    handleToggleActive,
  } = useGroupSeatManagement();

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">로딩 중...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      {/* 헤더 */}
      <div className="mb-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">그룹 좌석 관리</h1>
            <p className="mt-2 text-sm text-gray-600">
              스터디룸, 회의실 등 그룹 좌석을 관리합니다
            </p>
          </div>
          <button
            onClick={openCreateModal}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
          >
            <PlusIcon className="h-5 w-5" />새 그룹 추가
          </button>
        </div>
      </div>

      {/* 에러 메시지 */}
      {error && (
        <div className="mb-6 rounded-md bg-red-50 p-4">
          <div className="flex">
            <ExclamationTriangleIcon className="h-5 w-5 text-red-400" />
            <div className="ml-3">
              <h3 className="text-sm font-medium text-red-800">오류</h3>
              <div className="mt-2 text-sm text-red-700">
                <p>{error}</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* 그룹 목록 */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {groups.map((group) => (
          <div key={group.id} className="bg-white rounded-lg shadow p-6">
            <div className="flex items-start justify-between mb-4">
              <div className="flex-1">
                <h3 className="text-lg font-medium text-gray-900">{group.name}</h3>
                {group.description && (
                  <p className="text-sm text-gray-500 mt-1">{group.description}</p>
                )}
              </div>
              <span
                className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                  group.is_active
                    ? 'bg-green-100 text-green-800'
                    : 'bg-red-100 text-red-800'
                }`}
              >
                {group.is_active ? '활성' : '비활성'}
              </span>
            </div>

            {/* 그룹 타입 */}
            <div className="mb-4">
              <span
                className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${groupSeatManagementService.getGroupTypeColor(
                  group.group_type
                )}`}
              >
                {groupSeatManagementService.getGroupTypeLabel(group.group_type)}
              </span>
            </div>

            {/* 그룹 정보 */}
            <div className="space-y-2 text-sm text-gray-600 mb-4">
              <div className="flex items-center justify-between">
                <span className="flex items-center gap-1">
                  <UserGroupIcon className="h-4 w-4" />
                  수용 인원
                </span>
                <span className="font-medium">{group.capacity}명</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="flex items-center gap-1">
                  <CurrencyDollarIcon className="h-4 w-4" />
                  시간당 요금
                </span>
                <span className="font-medium">
                  {groupSeatManagementService.formatHourlyRate(group.hourly_rate)}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="flex items-center gap-1">
                  <BuildingOfficeIcon className="h-4 w-4" />
                  좌석
                </span>
                <span className="font-medium">
                  {groupSeatManagementService.formatSeatNumbers(group.seats)}
                </span>
              </div>
            </div>

            {/* 액션 버튼 */}
            <div className="flex items-center gap-2">
              <button
                onClick={() => openEditModal(group)}
                className="flex-1 flex items-center justify-center gap-1 px-3 py-2 text-sm border border-gray-300 rounded-md hover:bg-gray-50"
              >
                <PencilIcon className="h-4 w-4" />
                수정
              </button>
              <button
                onClick={() => handleToggleActive(group.id, group.is_active)}
                className={`flex items-center justify-center gap-1 px-3 py-2 text-sm border rounded-md ${
                  group.is_active
                    ? 'border-red-300 text-red-600 hover:bg-red-50'
                    : 'border-green-300 text-green-600 hover:bg-green-50'
                }`}
              >
                {group.is_active ? (
                  <XMarkIcon className="h-4 w-4" />
                ) : (
                  <CheckCircleIcon className="h-4 w-4" />
                )}
              </button>
              <button
                onClick={() => handleDeleteGroup(group.id)}
                className="flex items-center justify-center gap-1 px-3 py-2 text-sm border border-red-300 text-red-600 rounded-md hover:bg-red-50"
              >
                <TrashIcon className="h-4 w-4" />
              </button>
            </div>
          </div>
        ))}
      </div>

      {/* 그룹 생성/수정 모달 */}
      {showModal && (
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
            <h3 className="text-lg font-medium text-gray-900 mb-4">
              {editingGroup ? '그룹 수정' : '새 그룹 추가'}
            </h3>

            <div className="space-y-4">
              {/* 그룹 이름 */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  그룹 이름 *
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="예: 스터디룸 A"
                />
              </div>

              {/* 설명 */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  설명
                </label>
                <textarea
                  value={formData.description}
                  onChange={(e) =>
                    setFormData({ ...formData, description: e.target.value })
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="그룹에 대한 설명을 입력하세요"
                  rows={3}
                />
              </div>

              {/* 그룹 타입 */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  그룹 타입 *
                </label>
                <select
                  value={formData.group_type}
                  onChange={(e) =>
                    setFormData({ ...formData, group_type: e.target.value as any })
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="study_room">스터디룸</option>
                  <option value="meeting_room">회의실</option>
                  <option value="conference_room">컨퍼런스룸</option>
                  <option value="private_room">개인룸</option>
                </select>
              </div>

              {/* 수용 인원 및 시간당 요금 */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    수용 인원 *
                  </label>
                  <input
                    type="number"
                    value={formData.capacity}
                    onChange={(e) =>
                      setFormData({ ...formData, capacity: parseInt(e.target.value) })
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    min="1"
                    max="20"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    시간당 요금 *
                  </label>
                  <input
                    type="number"
                    value={formData.hourly_rate}
                    onChange={(e) =>
                      setFormData({ ...formData, hourly_rate: parseInt(e.target.value) })
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    min="0"
                    step="1000"
                  />
                </div>
              </div>

              {/* 좌석 선택 */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  좌석 선택 * ({selectedSeats.length}개 선택됨)
                </label>
                <div className="max-h-64 overflow-y-auto border border-gray-300 rounded-md p-3">
                  <div className="grid grid-cols-4 sm:grid-cols-6 md:grid-cols-8 gap-2">
                    {availableSeats.map((seat) => (
                      <button
                        key={seat.seat_number}
                        onClick={() => toggleSeatSelection(seat.seat_number)}
                        disabled={!!(seat.group_id && seat.group_id !== editingGroup?.id)}
                        className={`px-2 py-1 text-sm rounded-md border ${
                          selectedSeats.includes(seat.seat_number)
                            ? 'bg-blue-600 text-white border-blue-600'
                            : seat.group_id && seat.group_id !== editingGroup?.id
                              ? 'bg-gray-200 text-gray-400 border-gray-300 cursor-not-allowed'
                              : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                        }`}
                      >
                        {seat.seat_number}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {/* 모달 버튼 */}
            <div className="mt-6 flex items-center gap-3">
              <button
                onClick={handleSaveGroup}
                className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
              >
                {editingGroup ? '수정' : '추가'}
              </button>
              <button
                onClick={closeModal}
                className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50"
              >
                취소
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default GroupSeatManagement;
